// src/pages/billing/BillingDetailPage.tsx
// Chart-of-accounts account detail — inline-edit property grid, the only
// edit surface (see docs/ux/14-forms.md). UpdateAccountRequest only carries
// name/description/is_active/metadata, so those are the only fields editable
// here; code/type/subtype/parent/currency/balances are structural/computed
// and rendered read-only.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBilling, useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Alert, ErrorAlert } from '@react/components';
import { FormRow, InlineEditText } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { AccountBalanceCard } from '@/features/billing/AccountBalanceCard';
import { formatMoney, humanizeEnum, errMessage } from '@/features/billing/format';

/**
 * Chart-of-accounts account detail page.
 */
export const BillingDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBilling();
  const { t } = useI18n();
  const item = store.currentItem as any;
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      store.GetAccount(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  const commit = async (fields: Record<string, unknown>) => {
    if (!id) return;
    setSaveError(null);
    try {
      await store.UpdateAccount(id, { entity: item?.entity, field_mask: [], ...fields } as any);
    } catch (err) {
      setSaveError(errMessage(err, t('billing.chartOfAccounts.saveError')));
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/billing/chart-of-accounts" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/billing/chart-of-accounts" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/billing/chart-of-accounts" className="text-muted-foreground hover:text-foreground">
            {t('billing.chartOfAccounts.title')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="font-mono text-foreground">{String(item.code ?? '')}</span>
        </nav>
        <InlineEditText
          value={String(item.name ?? '')}
          onCommit={(next) => commit({ name: next })}
          variant="title"
          placeholder={t('billing.chartOfAccounts.namePlaceholder')}
          aria-label={t('common.name')}
        />
      </div>

      {saveError && <ErrorAlert error={saveError} dismissible onDismiss={() => setSaveError(null)} />}

      <Card>
        <CardHeader>
          <CardTitle>{t('billing.details')}</CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <FormRow label={t('billing.chartOfAccounts.code')} labelWidth="sm">
            <span className="font-mono text-sm text-foreground">{String(item.code ?? '--')}</span>
          </FormRow>
          <FormRow label={t('common.description')} labelWidth="sm">
            <InlineEditText
              value={String(item.description ?? '')}
              onCommit={(next) => commit({ description: next })}
              allowEmpty
              placeholder={t('billing.chartOfAccounts.descriptionPlaceholder')}
              aria-label={t('common.description')}
            />
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.type')} labelWidth="sm">
            <span className="text-sm text-foreground">{humanizeEnum(item.type, 'ACCOUNT_TYPE_') || '--'}</span>
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.subtype')} labelWidth="sm">
            <span className="text-sm text-foreground">{humanizeEnum(item.subtype, 'ACCOUNT_SUBTYPE_') || '--'}</span>
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.parent')} labelWidth="sm">
            <span className="font-mono text-sm text-foreground">{item.parent?.id || '--'}</span>
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.currency')} labelWidth="sm">
            <span className="text-sm text-foreground">{humanizeEnum(item.currency, 'CURRENCY_') || '--'}</span>
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.isSystem')} labelWidth="sm">
            <span className="text-sm text-foreground">{item.is_system ? t('common.yes') : t('common.no')}</span>
          </FormRow>
          <FormRow label={t('common.status')} labelWidth="sm">
            <ToggleSwitch
              label={item.is_active ? t('common.active') : t('common.inactive')}
              checked={!!item.is_active}
              onChange={(checked) => commit({ is_active: checked })}
            />
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.allowManualEntries')} labelWidth="sm">
            <span className="text-sm text-foreground">{item.allow_manual_entries ? t('common.yes') : t('common.no')}</span>
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.currentBalance')} labelWidth="sm">
            <span className="text-sm text-foreground">{formatMoney(item.current_balance)}</span>
          </FormRow>
          <FormRow label={t('billing.chartOfAccounts.pendingBalance')} labelWidth="sm">
            <span className="text-sm text-foreground">{formatMoney(item.pending_balance)}</span>
          </FormRow>
          <FormRow label={t('common.metadata')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">
              {item.metadata && Object.keys(item.metadata).length > 0
                ? Object.entries(item.metadata).map(([k, v]) => `${k}=${v}`).join(', ')
                : '--'}
            </span>
          </FormRow>
          <FormRow label={t('common.createdAt')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">{String(item.created_at ?? '--')}</span>
          </FormRow>
          <FormRow label={t('common.updatedAt')} labelWidth="sm">
            <span className="text-sm text-muted-foreground">{String(item.updated_at ?? '--')}</span>
          </FormRow>
        </CardContent>
      </Card>

      {/* Balance — point-in-time balance for this Chart-of-Accounts account (GetAccountBalance) */}
      {id && <AccountBalanceCard accountId={id} />}
    </div>
  );
});

export default BillingDetailPage;
